import { Data, List, Misc, ModalUtils } from '@singularsystems/neo-core';
import { Routing, Views } from '@singularsystems/neo-react';
import { NotificationDuration } from "../../../App/Models/NotificationDuration";
import { AppService, DomainTypes, Types } from '../../DomainTypes';
import LegalEntityType from "../../Models/Catalogue/Classifications/LegalEntityType";
import Investor from "../../Models/Investors/Investor";
import InvestorPagedLookup from "../../Models/Investors/Lookups/InvestorPagedLookup";
import InvestorPagedLookupCriteria from "../../Models/Investors/Lookups/InvestorPagedLookupCriteria";
import NoteVM from '../../Models/Notes/NoteVM';
import AuthorisationVM from '../../Services/AuthorisationVM';
import PortalUserLookup from "../../Models/PortalUser/Lookups/PortalUserLookup";
import InvestorReferralType from '../../Models/Catalogue/Investments/InvestorReferralType';
import { ReportingConfigValues } from "../../Models/Config/ReportingConfig";

export default class InvestorVM extends Views.ViewModelBase {

    constructor(
        taskRunner = AppService.get(Types.Neo.TaskRunner),
        public dataCache = AppService.get(DomainTypes.Services.DataCache),
        public contactsApiClient = AppService.get(DomainTypes.ApiClients.Contact),
        private notifications = AppService.get(Types.Neo.UI.GlobalNotifications),
        private investorApiClient = AppService.get(DomainTypes.ApiClients.Investor),
        private configApiClient = AppService.get(DomainTypes.ApiClients.Config),
        private portalUserApiClient = AppService.get(DomainTypes.ApiClients.PortalUser),
        protected investmentApiClient = AppService.get(Types.Domain.ApiClients.CatalogueInvestment)) {
        
        super(taskRunner);
        this.autoRunner = this.autoDispose(this.criteria.onAnyPropertyChanged(() => this.pageManager.refreshData()))
        this.makeObservable();
    }

    public noteVM = this.registerViewModel(NoteVM);
    public authorisationVM = this.registerViewModel(AuthorisationVM);

    public investorPageList = new List(InvestorPagedLookup);
    public criteria = new InvestorPagedLookupCriteria();
    public editingInvestor: Investor | null = null;
    public autoRunner?: Misc.IAutoRunner;
    public reportingConfigValues: ReportingConfigValues | null = null;
    public static lastInvestor?: Investor;

    public pageManager = new Data.PageManager(this.criteria, InvestorPagedLookup, this.investorApiClient.getInvestorLookupsPaged, {
        pageSize: 20,
        initialTaskRunner: this.taskRunner,
        fetchInitial: false,
        allowSort: true,
        sortBy: "investorName"
    });

    public portalUserLookupDataSource = new Data.ApiClientLookupDataSource(
        () => this.portalUserApiClient.getPortalUserLookups(false),
        PortalUserLookup
    );

    public async onInvestorParamChanged(investorParam: Routing.IViewParameter) {
        if (investorParam.value) {
            if (investorParam.value === "new") {
                this.newInvestor();
                investorParam.description = "New Investor";
            } else {
                await this.fetchInvestor(investorParam.value as number);
                investorParam.description = this.editingInvestor!.investorName;
            }
        } else {
            this.editingInvestor = null;
        }
    }

    private async newInvestor() {
        const response = await this.taskRunner.waitFor(this.configApiClient.getReportingConfig());
        const reportingConfigValues = ReportingConfigValues.fromJSObject<ReportingConfigValues>(response.data);
        this.editingInvestor = new Investor();

        if (reportingConfigValues?.withholdingAgentConfig) {
            this.editingInvestor.beneficiaryName = reportingConfigValues.withholdingAgentConfig.beneficiaryName;
            this.editingInvestor.withholdingAgentRefNumber = reportingConfigValues.withholdingAgentConfig.withholdingAgentRefNumber;
            this.editingInvestor.beneficiaryTaxReferenceNumber = reportingConfigValues.withholdingAgentConfig.beneficiaryTaxReferenceNumber;
            this.editingInvestor.beneficiaryPhysicalAddress = reportingConfigValues.withholdingAgentConfig.beneficiaryPhysicalAddress;
        }
    }

    private async fetchInvestor(investorId: number) {
        const response = await this.taskRunner.waitFor(this.investorApiClient.getInvestor(investorId));
        this.editingInvestor = Investor.fromJSObject<Investor>(response.data);
    }

    public toggleBankDetails() {
        if (this.editingInvestor!.additionalBankDetails.length === 0) {
            this.editingInvestor!.additionalBankDetails.addNew();
        } else {
            this.editingInvestor!.additionalBankDetails.remove(this.editingInvestor!.additionalBankDetails[0]);
        }
    }

    public assignLegalEntity(legalEntityType?: LegalEntityType) {
        if (this.editingInvestor) {
            this.editingInvestor.isIndividual = legalEntityType?.isIndividual ?? false;
            this.editingInvestor.isTrust = legalEntityType?.isTrust ?? false;
            this.editingInvestor.investorName = "";
            this.editingInvestor.firstName = "";
            this.editingInvestor.lastName = "";
            this.editingInvestor.titleId = null;
        }
    }

    public async saveInvestor() {
        this.taskRunner.run(async () => {
            const response = await this.investorApiClient.saveInvestor(this.editingInvestor!.beforeSerialise().toJSObject());
            this.editingInvestor!.update(response.data);
            this.pageManager.refreshData();
            this.notifications.addSuccess("Saved Successfully", "Investor saved successfully.", NotificationDuration.Standard);
        });
    }

    public async investorReferralTypeSelected(investorReferralType?: InvestorReferralType) {
        
        if (this.editingInvestor) {
            this.editingInvestor.requiresExternalAccountNo = investorReferralType?.requiresExternalAccountNo ?? false;

            if (investorReferralType?.bankDetails) {
                this.editingInvestor.bankDetails = investorReferralType.bankDetails.clone();
            }
        }
    }

    public setInvestorPendingDelete(investor: Investor) {
        investor.isPendingDelete = !investor.isPendingDelete;
        if (investor.isPendingDelete) {
            this.notifications.addInfo("Mark Deleted", `Please click save to action this request.`, NotificationDuration.Standard);
        }
    }
}